import React, { useState, useEffect } from "react";
import { X, Swords, Coins, Clock } from "lucide-react";
import api from "../lib/api.js";
import { toast } from "sonner";

const DURATIONS = [6, 12, 24, 48, 72];
const STAKES = [50, 100, 250, 500, 1000];

export default function NewDuelModal({ open, onClose, onCreated }) {
  const [friends, setFriends] = useState([]);
  const [opponent, setOpponent] = useState(null);
  const [stake, setStake] = useState(100);
  const [duration, setDuration] = useState(24);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.get("/friends").then((r) => setFriends(r.data));
    setOpponent(null);
    setMessage("");
  }, [open]);

  if (!open) return null;

  const submit = async () => {
    if (!opponent) return toast.error("Pick a friend to duel");
    setBusy(true);
    try {
      const { data } = await api.post("/duels", {
        challenged_id: opponent.id,
        stake,
        duration_hours: duration,
        message,
      });
      toast.success(`Duel sent to ${opponent.name}`);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create duel");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-4 bg-black/70 backdrop-blur-sm ps-fade-up" data-testid="new-duel-modal">
      <div className="ps-card-elev p-6 w-full max-w-md max-h-[90vh] overflow-y-auto relative">
        <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-white/5" data-testid="new-duel-close">
          <X size={18} className="text-slate-400" />
        </button>

        <div className="ps-chip mb-2"><Swords size={11} /> Posture Duel</div>
        <h2 className="ps-heading text-2xl font-extrabold">
          Challenge a <span className="ps-serif ps-italic font-normal">friend</span>
        </h2>
        <p className="text-xs text-slate-400 ps-serif ps-italic mt-1">Highest steps in the window wins the pot.</p>

        {/* Friend picker */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Opponent</div>
          {friends.length === 0 ? (
            <div className="text-sm text-slate-400 ps-serif ps-italic">No friends yet — add one first.</div>
          ) : (
            <div className="grid grid-cols-3 gap-2">
              {friends.map((f) => {
                const sel = opponent?.id === f.id;
                return (
                  <button
                    key={f.id}
                    onClick={() => setOpponent(f)}
                    data-testid={`new-duel-friend-${f.id}`}
                    className={`p-3 rounded-2xl border flex flex-col items-center gap-1 transition-all ${sel ? "border-[#4ade80] bg-[#4ade80]/10 ring-2 ring-[#4ade80]/30" : "border-[#1B364A] hover:border-[#4ade80]/40"}`}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#15303E] to-[#0E1F2A] border border-[#1B364A] flex items-center justify-center font-bold text-sm">
                      {f.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="text-[10px] font-semibold truncate w-full text-center">{f.name.split(" ")[0]}</div>
                    <div className="text-[9px] text-slate-500 ps-tabular">{f.points}pts</div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Stake */}
        <div className="mt-5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
            <Coins size={11} color="#FBBF24" /> Stake (each)
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {STAKES.map((s) => (
              <button
                key={s}
                onClick={() => setStake(s)}
                data-testid={`new-duel-stake-${s}`}
                className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${stake === s ? "bg-[#FBBF24]/15 text-[#FBBF24] border-[#FBBF24]/40" : "bg-[#0E1F2A]/40 text-slate-400 border-[#1B364A]"}`}
              >{s}</button>
            ))}
          </div>
          <div className="text-[10px] text-slate-500 ps-serif ps-italic mt-1.5">
            Pot if you win: <span className="text-[#4ade80] font-bold">+{stake * 2 + 50}</span> · You lose <span className="text-[#ef4444] font-bold">-{stake}</span> if you lose.
          </div>
        </div>

        {/* Duration */}
        <div className="mt-5">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">
            <Clock size={11} color="#a78bfa" /> Duration
          </div>
          <div className="flex gap-1.5">
            {DURATIONS.map((d) => (
              <button
                key={d}
                onClick={() => setDuration(d)}
                data-testid={`new-duel-duration-${d}`}
                className={`flex-1 px-2 py-1.5 rounded-full text-xs font-bold border transition-colors ${duration === d ? "bg-[#a78bfa]/15 text-[#a78bfa] border-[#a78bfa]/40" : "bg-[#0E1F2A]/40 text-slate-400 border-[#1B364A]"}`}
              >{d}h</button>
            ))}
          </div>
        </div>

        {/* Trash talk */}
        <div className="mt-5">
          <div className="text-[10px] uppercase tracking-widest text-slate-400 font-bold mb-2">Message (optional)</div>
          <input
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={200}
            placeholder="Bring your A-game…"
            data-testid="new-duel-message"
            className="w-full bg-[#0E1F2A]/60 border border-[#1B364A] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#4ade80]/50"
          />
        </div>

        <button
          onClick={submit}
          disabled={busy || !opponent}
          data-testid="new-duel-submit"
          className="ps-button-primary mt-6 w-full disabled:opacity-50"
        >
          {busy ? "Sending…" : `Send challenge${opponent ? ` to ${opponent.name.split(" ")[0]}` : ""}`}
        </button>
      </div>
    </div>
  );
}
